import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { SecurityQuestion } from '@ps/web-shared-api-types/v0/RAM2';
import { StarsButton, Translation } from '@ps/web-shared-components';
import { StarsInfo, createForm } from '@ps/web-shared-ui';
import type { Error } from '@ps/web-shared-validators';
import classNames from 'clsx';
import type { FormApi } from 'final-form';
import React, { useEffect, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { scrollToTop } from '@swf/core/utils';
import { buildFormErrors } from '@swf/core/utils/forms';

import ChangePasswordInputs from './ChangePasswordInputs/ChangePasswordInputs';
import {
  TXTCLI_Apply__forChanges,
  TXTCLI_Passwords_do_not_match,
  TXTCLI_The_password_you_have_typed_in_ELL,
  TXTWCL_Your_changes_have_been_saved_s_ELL
} from './StarsChangePassword.messages.json';
import type { Question } from './StrongPwdReset/StrongPwdReset';
import StrongPwdReset from './StrongPwdReset/StrongPwdReset';

interface Props {
  className?: string;
  oldPassword?: string;
  successCallback?: (newPassword: string) => void;
}

interface FormValues {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  answer0?: string;
  answer1?: string;
  ssn?: string;
}

const Form = createForm<FormValues>();

const errorTranslations = {
  invalidPassword: TXTCLI_The_password_you_have_typed_in_ELL,
  wrongPassword: TXTCLI_The_password_you_have_typed_in_ELL
};

const StarsChangePassword: React.FC<Props> = ({ className, oldPassword, successCallback }) => {
  const { account } = useDispatch<Dispatch>();
  const disableFormUS = useSelector(select.account.disableFormUS);
  const isStrongAuthRequired = useFeature(FeatureFlag.StrongAuthQuestionsPwdReset);
  const [questions, setQuestions] = useState<Question[]>([]);
  const hasQuestions = questions?.length > 0;
  const request = useRequest();

  const validate = (values: FormValues) => {
    if (
      values.newPassword &&
      values.confirmNewPassword &&
      values.newPassword !== values.confirmNewPassword
    ) {
      return {
        confirmNewPassword: {
          valid: false,
          message: TXTCLI_Passwords_do_not_match
        } as Error
      };
    }
    return {};
  };

  useEffect(() => {
    if (isStrongAuthRequired) {
      request<SecurityQuestion[]>({
        service: '/api/v1-preview/auth/password/change/security-questions',
        method: 'GET'
      }).subscribe((response) => {
        setQuestions(response.body);
      });
    }
  }, [request, isStrongAuthRequired]);

  const onSubmit = async (values: FormValues, formApi: FormApi<FormValues>) => {
    let response;
    if (isStrongAuthRequired && hasQuestions) {
      response = await account.updateStrongAuthPassword({
        newPassword: values.newPassword,
        oldPassword: oldPassword || values.oldPassword,
        answer1: values.answer0 as string,
        answer2: values.answer1 as string,
        ssn: values?.ssn as string
      });
    } else {
      response = await account.updatePassword({
        password: values.newPassword,
        oldPassword: oldPassword || values.oldPassword
      });
    }

    scrollToTop();

    if (response && response.success) {
      formApi.initialize({} as FormValues);

      if (successCallback) {
        successCallback(values.newPassword);
      }

      Object.keys(values).forEach((key) => {
        formApi.resetFieldState(key as keyof FormValues);
      });
    }

    return buildFormErrors(response, errorTranslations);
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      className={className}
      render={({ form }) => {
        const { submitError, submitSucceeded, submitErrors, submitting, pristine } =
          form.getState();

        return (
          <>
            {submitSucceeded && (
              <StarsInfo type="confirmation" className={sharedStyles.message}>
                <Translation {...TXTWCL_Your_changes_have_been_saved_s_ELL} />
              </StarsInfo>
            )}
            {submitError && !submitErrors?.oldPassword && (
              <StarsInfo type="error" className={sharedStyles.message}>
                {submitError}
              </StarsInfo>
            )}
            {isStrongAuthRequired && hasQuestions && <StrongPwdReset data={questions} />}
            <ChangePasswordInputs hideOldPasswordField={!!oldPassword} />
            <FormSpy subscription={{ values: true }}>
              {({ form: formApi }) => {
                const { values } = formApi.getState();

                return (
                  <StarsButton
                    block
                    className={classNames(sharedStyles.submit)}
                    disabled={
                      disableFormUS ||
                      pristine ||
                      submitting ||
                      (values.oldPassword || oldPassword) === values.newPassword ||
                      (isStrongAuthRequired &&
                        hasQuestions &&
                        (!values.answer0 || !values.answer1 || !values.ssn))
                    }
                    type="submit"
                    translation={TXTCLI_Apply__forChanges}
                  />
                );
              }}
            </FormSpy>
          </>
        );
      }}
    />
  );
};

export default StarsChangePassword;
